import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { resolveAdminNavModule } from "../config/adminModules";
import { patchCurrentUser } from "../redux/userRelated/userSlice";
import {
    DEFAULT_FEATURE_PREFERENCES,
    isModuleEnabledInPreferences,
    parseFeaturePreferences,
    sameFeaturePreferences,
} from "./featurePreferences";
import {
    readAdminFeaturePreferences,
    writeAdminFeaturePreferences,
} from "../utils/adminFeaturePreferencesStorage";

const defaultContextValue = {
    mode: DEFAULT_FEATURE_PREFERENCES.mode,
    enabledModules: DEFAULT_FEATURE_PREFERENCES.enabledModules,
    isModuleEnabled: () => true,
    isFeatureEnabled: () => true,
    preferences: DEFAULT_FEATURE_PREFERENCES,
    savePreferences: async () => ({ ok: false, message: "Not available" }),
};

export const FeatureContext = createContext({
    ...defaultContextValue,
    enabledModules: DEFAULT_FEATURE_PREFERENCES.enabledModules,
});

function resolveInitialPreferences(adminId, sessionRaw) {
    const stored = readAdminFeaturePreferences(adminId);
    if (stored) return stored;
    return parseFeaturePreferences(sessionRaw);
}

export function FeatureProvider({ children }) {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user?.currentUser);
    const rehydrated = useSelector((state) => state._persist?.rehydrated);
    const adminId = currentUser?._id || currentUser?.id;
    const isAdmin = currentUser?.role === "Admin";

    const syncedAdminRef = useRef(null);
    const adminIdRef = useRef(adminId);

    const [preferences, setPreferences] = useState(() =>
        resolveInitialPreferences(adminId, currentUser?.featurePreferences),
    );

    // Switch accounts or sign out — load that account's saved prefs once.
    useEffect(() => {
        if (!adminId) {
            adminIdRef.current = null;
            syncedAdminRef.current = null;
            setPreferences({ ...DEFAULT_FEATURE_PREFERENCES });
            return;
        }
        if (adminIdRef.current === adminId) return;
        adminIdRef.current = adminId;
        syncedAdminRef.current = null;
        setPreferences(resolveInitialPreferences(adminId, currentUser?.featurePreferences));
    }, [adminId, currentUser?.featurePreferences]);

    // Background server sync once per admin (no UI loading state — avoids Strict Mode spinner stuck).
    useEffect(() => {
        if (!rehydrated || !isAdmin || !adminId) return;
        if (syncedAdminRef.current === adminId) return;

        let cancelled = false;
        const localPrefs = readAdminFeaturePreferences(adminId);

        if (localPrefs) {
            syncedAdminRef.current = adminId;
            setPreferences(localPrefs);
            dispatch(patchCurrentUser({ featurePreferences: localPrefs }));
            return undefined;
        }

        const syncFromServer = async () => {
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/Admin/${adminId}`, {
                    timeout: 12000,
                });
                if (cancelled) return;

                // User may have saved modules while this request was in flight.
                if (readAdminFeaturePreferences(adminId)) return;

                const hasEntity = Boolean(data?.id || data?._id || data?.schoolId);
                if (!hasEntity && data?.message) return;

                const serverPrefs = parseFeaturePreferences(data?.featurePreferences);
                if (serverPrefs.mode !== "admin-only") return;

                writeAdminFeaturePreferences(adminId, serverPrefs);
                setPreferences(serverPrefs);
                dispatch(patchCurrentUser({ featurePreferences: serverPrefs }));
            } catch {
                /* keep session / default copy */
            } finally {
                if (!cancelled) {
                    syncedAdminRef.current = adminId;
                }
            }
        };

        syncFromServer();

        return () => {
            cancelled = true;
        };
    }, [rehydrated, isAdmin, adminId, dispatch]);

    const savePreferences = useCallback(
        async (nextPreferences) => {
            if (!adminId) {
                return { ok: false, message: "Not signed in" };
            }

            const parsed = parseFeaturePreferences(nextPreferences);

            const applyLocal = (prefs) => {
                writeAdminFeaturePreferences(adminId, prefs);
                setPreferences(prefs);
                dispatch(patchCurrentUser({ featurePreferences: prefs }));
                syncedAdminRef.current = adminId;
            };

            try {
                const { data } = await axios.put(
                    `${getApiBaseUrl()}/Admin/${adminId}`,
                    { featurePreferences: parsed },
                    {
                        headers: { "Content-Type": "application/json" },
                        timeout: 12000,
                    },
                );

                const hasEntity = Boolean(data?.id || data?._id || data?.schoolId);
                if (data?.message && !hasEntity) {
                    return { ok: false, message: data.message };
                }

                // Always apply what the user saved. The API often echoes the DB default
                // (mode: "full") until featurePreferences is persisted server-side.
                applyLocal(parsed);

                const serverPrefs =
                    data?.featurePreferences != null
                        ? parseFeaturePreferences(data.featurePreferences)
                        : null;
                if (serverPrefs && sameFeaturePreferences(serverPrefs, parsed)) {
                    applyLocal(serverPrefs);
                }

                return { ok: true, data: parsed };
            } catch (e) {
                const message =
                    e.response?.data?.message || e.message || "Could not save module preferences";
                return { ok: false, message };
            }
        },
        [adminId, dispatch],
    );

    const isModuleEnabled = useCallback(
        (moduleId) => {
            if (!isAdmin) return true;
            return isModuleEnabledInPreferences(preferences, moduleId);
        },
        [isAdmin, preferences],
    );

    const isFeatureEnabled = useCallback(
        (featureOrRoute) => {
            if (!isAdmin) return true;
            if (String(featureOrRoute || "").startsWith("/")) {
                const moduleId = resolveAdminNavModule(featureOrRoute);
                return isModuleEnabledInPreferences(preferences, moduleId);
            }
            return isModuleEnabledInPreferences(preferences, featureOrRoute);
        },
        [isAdmin, preferences],
    );

    const value = useMemo(
        () => ({
            mode: preferences.mode,
            enabledModules: preferences.enabledModules,
            isModuleEnabled,
            isFeatureEnabled,
            preferences,
            savePreferences,
        }),
        [preferences, isModuleEnabled, isFeatureEnabled, savePreferences],
    );

    return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
}

export function useFeatureContext() {
    return useContext(FeatureContext);
}

export function useFeatures() {
    return useContext(FeatureContext);
}
